'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Card } from './Card'
import type { CardsBlock } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { ArrowButton } from '@/components/ui/arrow-button'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'

const DESKTOP_VISIBLE = 3

type CardStyleStandardAccordionProps = {
  cards: NonNullable<CardsBlock['cards']>
  paddingTop?: boolean
  paddingBottom?: boolean
}

// Card stacking configuration
// These values control the visual spacing and sizing of the accordion stack
const CARD_BORDER_RADIUS_OVERLAP = 16      // Amount cards overlap each other (matches border-radius)
const COLLAPSED_CARD_STACK_SPACING = 140   // Vertical spacing between stacked collapsed cards (shows title + breathing room)
const EXPANDED_CARD_MAX_HEIGHT = 220       // Max height constraint for expanded cards (for layout)

export const CardStyleStandardAccordion: React.FC<CardStyleStandardAccordionProps> = ({
  cards,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  // State for mobile accordion - responsive initial state
  const [expandedCardIndex, setExpandedCardIndex] = useState<number | null>(null)

  // Track measured heights for each card (indexed by cardIndex)
  const [cardMeasurements, setCardMeasurements] = useState<Record<number, {
    collapsedHeight: number
    stackSpacing: number
    expandedHeight?: number
  }>>({})

  // Handler to receive measurements from Card components (memoized to prevent infinite loops)
  const handleHeightMeasure = useCallback(
    (cardIndex: number, collapsedHeight: number, stackSpacing: number, expandedHeight?: number) => {
      setCardMeasurements(prev => ({
        ...prev,
        [cardIndex]: {
          collapsedHeight,
          stackSpacing,
          // Preserve previous measurement when this call doesn't include it
          expandedHeight: expandedHeight ?? prev[cardIndex]?.expandedHeight,
        },
      }))
    },
    [],
  )

  // Set initial expanded state based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      // Desktop breakpoint is 'md' (768px) to match the component's responsive breakpoint
      const isDesktop = window.matchMedia('(min-width: 768px)').matches
      // Start with the last card expanded on mobile
      setExpandedCardIndex(isDesktop ? null : (cards?.length || 1) - 1)
    }

    // Check on mount
    checkScreenSize()

    // Optional: Listen for screen size changes (if user resizes window)
    const mediaQuery = window.matchMedia('(min-width: 768px)')
    const handleChange = () => checkScreenSize()
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [cards?.length])

  const handleCardToggle = (index: number) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index)
  }

  // Get spacing for a card (use measured value or fallback to default)
  const getCardSpacing = (cardIndex: number) => {
    return cardMeasurements[cardIndex]?.stackSpacing || COLLAPSED_CARD_STACK_SPACING
  }

  // Get expanded height for a card (use measured natural height or fallback)
  const getExpandedHeight = (cardIndex: number) => {
    return cardMeasurements[cardIndex]?.expandedHeight || EXPANDED_CARD_MAX_HEIGHT
  }

  // Calculate top offset for each card based on cumulative heights above
  const getTopOffset = (cardIndex: number, expandedIndex: number) => {
    let offset = 0

    // Sum heights of all cards above this one
    for (let i = 0; i < cardIndex; i++) {
      if (i === expandedIndex) {
        // Card above is expanded - use its measured natural height for proper spacing
        offset += getExpandedHeight(i) - CARD_BORDER_RADIUS_OVERLAP
      } else {
        // Card above is collapsed - use measured spacing
        offset += getCardSpacing(i)
      }
    }

    return offset
  }

  // Calculate required container height: all cards stacked (worst case: bottom card expanded)
  const containerHeight = cards.length > 0
    ? getTopOffset(cards.length, cards.length - 1)
    : 400

  const desktopIsCarousel = (cards?.length ?? 0) > DESKTOP_VISIBLE

  return (
    <div className={cn('box-border content-stretch flex flex-col gap-[42px] items-start justify-start relative w-full md:gap-2.5', paddingClasses)}>
      {/* Mobile: Accordion layout */}
      <div className="flex flex-col gap-0 w-full md:hidden">
        <div className="relative w-full" style={{ minHeight: `${containerHeight}px` }}>
          {cards?.map((card, index) => (
            <Card
              key={index}
              title={card.title}
              description={card.description || undefined}
              cta={card.cta || undefined}
              cardIndex={index}
              isExpanded={index === expandedCardIndex}
              isLast={index === (cards?.length || 0) - 1}
              topOffset={getTopOffset(index, expandedCardIndex ?? cards.length - 1)}
              zIndex={index}
              onToggle={() => handleCardToggle(index)}
              onHeightMeasure={handleHeightMeasure}
            />
          ))}
        </div>
      </div>

      {/* Desktop: 3-column grid when ≤3 cards */}
      {!desktopIsCarousel && (
        <div className="hidden md:grid md:grid-cols-3 md:gap-8 md:items-stretch md:w-full">
          {cards?.map((card, index) => (
            <Card
              key={index}
              title={card.title}
              description={card.description || undefined}
              cta={card.cta || undefined}
              cardIndex={index}
            />
          ))}
        </div>
      )}

      {/* Desktop: scroll-snap carousel when >3 cards */}
      {desktopIsCarousel && <DesktopCarousel cards={cards} />}
    </div>
  )
}

const DesktopCarousel: React.FC<{ cards: NonNullable<CardsBlock['cards']> }> = ({ cards }) => {
  const { scrollRef, activeIndex, isAtStart, isAtEnd, scrollToIndex, handleKeyDown } =
    useCarouselScroll({ itemCount: cards.length, autoplayDelay: 0 })

  return (
    <div className="hidden md:flex md:flex-col md:gap-8 w-full">
      <div
        ref={scrollRef}
        role="region"
        aria-label="Cards carousel"
        aria-roledescription="carousel"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative flex overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide w-full gap-8',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2',
        )}
      >
        {cards.map((card, index) => (
          <div
            key={index}
            data-card
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${cards.length}`}
            className="flex shrink-0 snap-start basis-[calc((100%-64px)/3)]"
          >
            <Card
              title={card.title}
              description={card.description || undefined}
              cta={card.cta || undefined}
              cardIndex={index}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 self-end" role="group" aria-label="Carousel navigation">
        <ArrowButton
          direction="left"
          backgroundColor="white"
          size="medium"
          className="border border-black"
          onClick={() => scrollToIndex(activeIndex - 1)}
          disabled={isAtStart}
          aria-label="Previous slide"
        />
        <ArrowButton
          direction="right"
          backgroundColor="white"
          size="medium"
          className="border border-black"
          onClick={() => scrollToIndex(activeIndex + 1)}
          disabled={isAtEnd}
          aria-label="Next slide"
        />
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {`Slide ${activeIndex + 1} of ${cards.length}`}
      </div>
    </div>
  )
}
